import { FC, useCallback, useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';

import SettingIcon from 'assets/icons/settings.svg';
import axios from 'axios';
import { Checkbox, SettingsModal, TokenLogo } from 'components';
import {
  NETWORK_SWITCH_DELAY,
  PRICE_IMPACT_GRADES,
  ROUTER_ADDRESSES,
  WOWMAX_API_URL,
} from 'consts';
import {
  useApprove,
  useBalances,
  useDebounce,
  useHandleConnectClick,
  useIsWrongNetwork,
  useSwap,
  useSwitchChain,
  useTokenAllowance,
  useWrapUnwrap,
} from 'hooks';
import { store } from 'store';
import { WowmaxSwapData } from 'types/aggregation';
import { bn } from 'utils/BigNumber';
import {
  addSeparators,
  cutAddress,
  cutTokenSymbol,
  getBalance,
  getScanUrl,
  isDefined,
  toDollars,
  wait,
} from 'utils/helpers';
import { Address, maxUint256 } from 'viem';

import { RoutingModal, SwapDetails, TokenSelectModal, UpdateButton } from './components';
import * as S from './Swap.styles';

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success('Address copied', { autoClose: 1500 });
  });
};

export const Swap: FC = observer(() => {
  const { resetMode } = store.commonStore;
  const { account, chainId, balances } = store.web3Store;
  const { requestingChainId, tokens } = store.commonStore;
  const {
    value,
    setValue,
    isNoValue,
    tokenIn,
    tokenOut,
    amountOut,
    amountOutView,
    inputInDollars,
    outputInDollars,
    flipTokens,
    wowmaxResult,
    hasPMMInRoutes,
    openSettings,
    hasSettingsWarning,
    settings,
    isTokenSelectOpen,
    isWrapUnwrap,
    isSwapping,
    isApproving,
    setIsSwapping,
    setIsApproving,
    openTokenSelect,
  } = store.swapStore;

  const isFirstBalancesCall = Object.values(balances).length < 2;
  useBalances({
    chainId,
    tokens:
      isFirstBalancesCall || isTokenSelectOpen
        ? tokens.map((t) => t.address)
        : [tokenIn?.address, tokenOut?.address].filter(isDefined),
    refetchIntervalSec: isTokenSelectOpen ? 15 : 5,
    enabled: !isSwapping && !isApproving,
  });

  const { handleConnectClick } = useHandleConnectClick();
  const { switchChain } = useSwitchChain();
  const isWrongNetwork = useIsWrongNetwork({ expectedChainId: chainId });
  const isFromNative = tokenIn?.tags?.includes('native');

  const routerAddress = ROUTER_ADDRESSES[chainId] as Address;
  const allowance = useTokenAllowance({
    chainId,
    tokenAddress: tokenIn?.address,
    spenderAddress: routerAddress,
    refetchIntervalSec: 5,
    enabled: !isSwapping && !isApproving,
  });
  const approve = useApprove({
    chainId,
    tokenAddress: tokenIn?.address,
    spenderAddress: routerAddress,
    allowance: +(allowance || 0),
  });
  const [isApproveInfinite, setIsApproveInfinite] = useState(true);
  const wrapUnwrap = useWrapUnwrap();
  const swap = useSwap();

  const balanceIn = getBalance(balances, tokenIn?.address) || '0';
  const balanceOut = getBalance(balances, tokenOut?.address) || '0';
  const isInsufficientBalance = !!tokenIn && bn(value).toDecimals(tokenIn.decimals).gt(balanceIn);
  const isInsufficientAllowance =
    !!tokenIn && allowance !== undefined && bn(value).toDecimals(tokenIn.decimals).gt(allowance);

  const [inputValue, setInputValue] = useState(value);
  const debouncedValue = useDebounce(inputValue);

  const isPriceImpactHigh = (wowmaxResult.data?.priceImpact || 0) >= PRICE_IMPACT_GRADES.warning;
  const isPriceImpactDanger = (wowmaxResult.data?.priceImpact || 0) >= PRICE_IMPACT_GRADES.danger;

  useEffect(() => {
    const value = debouncedValue.replaceAll(' ', '');
    setValue(value);
  }, [debouncedValue, setValue]);

  useEffect(() => {
    setInputValue(addSeparators(value));
  }, [value]);

  const handleInputChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value);
  }, []);

  const handleMaxClick = useCallback(() => {
    if (!tokenIn) return;
    setValue(bn(balanceIn).fromDecimals(tokenIn.decimals).toString());
  }, [tokenIn, balanceIn, setValue]);

  const handleApproveClick = async () => {
    if (!tokenIn) return;
    if (isWrongNetwork) {
      const isChanged = await switchChain(chainId);
      if (!isChanged) return;
    }
    setIsApproving(true);

    const roundedValue = bn(value).toSignificant(0, { signs: 8, roundUp: true });
    const approveAmount = isApproveInfinite
      ? maxUint256.toString()
      : bn(roundedValue).toDecimals(tokenIn.decimals).toString();

    await wait(isWrongNetwork ? NETWORK_SWITCH_DELAY : 0);
    await approve(approveAmount);
    setIsApproving(false);
  };

  const handleSwapClick = async () => {
    if (isWrongNetwork) {
      const isChanged = await switchChain(chainId);
      if (!isChanged) return;
    }
    setIsSwapping(true);

    if (isWrapUnwrap) {
      await wait(isWrongNetwork ? NETWORK_SWITCH_DELAY : 0);
      await wrapUnwrap();
      setIsSwapping(false);
      return;
    }

    if (!wowmaxResult.data || !tokenOut || !tokenIn) return;
    let request: WowmaxSwapData | null = null;

    try {
      const response = await axios.get<WowmaxSwapData>(`${WOWMAX_API_URL}/chains/${chainId}/swap`, {
        params: {
          amount: value,
          from: tokenIn.address,
          to: tokenOut.address,
          slippage: settings.slippage,
          usePMM: settings.usePMM,
          source: 'wowmax',
        },
      });
      request = response.data;
      console.log('swap request', request);
      if (bn(request.amountOut[0]).lt(bn(wowmaxResult.data.amountOut).mul(0.995))) {
        toast.info('Rates have changed. Please try again.');
        setIsSwapping(false);
        return;
      }
    } catch (err) {
      console.error('swap request error', err);
      toast.error(
        <>
          <b>An error has occurred.</b>
          <p>
            Please deactivate the option &quot;Boost rates with private liquidity&quot; in{' '}
            <S.ButtonInText onClick={openSettings}>settings</S.ButtonInText> and try again.
          </p>
        </>,
        { autoClose: false }
      );
    }

    if (request) {
      await swap({
        to: request.contract,
        data: request.data,
        value: request.value,
        gas: request.gasUnitsConsumed,
      });
    }
    setIsSwapping(false);
  };

  return (
    <S.Overlay>
      <S.PanelWrapper>
        <S.CloseButton onClick={resetMode}>✕</S.CloseButton>
        <S.Panel>
          <S.Header>
            <UpdateButton />
            <S.SettingsButton onClick={openSettings}>
              <SettingIcon />
              {hasSettingsWarning && <span className="settings-button__warning">⚠</span>}
            </S.SettingsButton>
          </S.Header>

          <S.Block>
            <S.BlockHeader>
              <S.BlockTitle>From</S.BlockTitle>
              {tokenIn && (
                <S.Balance>
                  <span>Balance: {bn(balanceIn).toView(tokenIn.decimals)}</span>
                  <S.MaxButton onClick={handleMaxClick} disabled={isSwapping}>
                    Max
                  </S.MaxButton>
                </S.Balance>
              )}
            </S.BlockHeader>
            <S.TokenLine>
              <S.TokenButton onClick={() => openTokenSelect('in')} disabled={isSwapping}>
                <TokenLogo token={tokenIn} size={20} />
                {tokenIn ? cutTokenSymbol(tokenIn.symbol) : 'Select'}
                <S.Chevron>▾</S.Chevron>
              </S.TokenButton>
              <NumericFormat
                value={inputValue}
                onChange={handleInputChange}
                inputMode="decimal"
                placeholder="0"
                thousandSeparator=" "
                allowNegative={false}
                allowedDecimalSeparators={['.', ',']}
                customInput={S.TokenInput}
                $isWarning={isPriceImpactDanger}
                disabled={isSwapping}
              />
            </S.TokenLine>
            <S.TokenSubLine>
              {tokenIn && (
                <S.TokenAddress>
                  {tokenIn.tags?.includes('native') ? (
                    <S.TokenNativeLabel>Native</S.TokenNativeLabel>
                  ) : (
                    <>
                      <S.TokenLink
                        href={getScanUrl(`/token/${tokenIn.address}`, chainId)}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {cutAddress(tokenIn.address)}
                      </S.TokenLink>
                      <S.CopyButton onClick={() => copyToClipboard(tokenIn.address)}>
                        ⧉
                      </S.CopyButton>
                    </>
                  )}
                </S.TokenAddress>
              )}
              <S.TokenValueInDollars>
                {!!inputInDollars && toDollars(inputInDollars)}
              </S.TokenValueInDollars>
            </S.TokenSubLine>
          </S.Block>

          <S.FlipButton onClick={flipTokens} disabled={isSwapping}>
            ⇄
          </S.FlipButton>

          <S.Block>
            <S.BlockHeader>
              <S.BlockTitle>To</S.BlockTitle>
              {tokenOut && (
                <S.Balance>
                  <span>Balance: {bn(balanceOut).toView(tokenOut.decimals)}</span>
                </S.Balance>
              )}
            </S.BlockHeader>
            <S.TokenLine>
              <S.TokenButton onClick={() => openTokenSelect('out')} disabled={isSwapping}>
                <TokenLogo token={tokenOut} size={20} />
                {tokenOut ? cutTokenSymbol(tokenOut.symbol) : 'Select'}
                <S.Chevron>▾</S.Chevron>
              </S.TokenButton>
              <S.TokenOutput>{amountOutView || ''}</S.TokenOutput>
            </S.TokenLine>
            <S.TokenSubLine>
              {tokenOut && (
                <S.TokenAddress>
                  {tokenOut.tags?.includes('native') ? (
                    <S.TokenNativeLabel>Native</S.TokenNativeLabel>
                  ) : (
                    <>
                      <S.TokenLink
                        href={getScanUrl(`/token/${tokenOut.address}`, chainId)}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {cutAddress(tokenOut.address)}
                      </S.TokenLink>
                      <S.CopyButton onClick={() => copyToClipboard(tokenOut.address)}>
                        ⧉
                      </S.CopyButton>
                    </>
                  )}
                </S.TokenAddress>
              )}
              <S.TokenValueInDollars>
                {!!outputInDollars && toDollars(outputInDollars)}
              </S.TokenValueInDollars>
            </S.TokenSubLine>
          </S.Block>

          <S.SwapDetailsWrapper>
            <SwapDetails />
          </S.SwapDetailsWrapper>

          {isPriceImpactHigh && (
            <S.Message $type={isPriceImpactDanger ? 'danger' : 'warning'}>
              {isPriceImpactDanger ? 'Price impact is too high' : 'Price impact is high'}
              <span style={{ marginLeft: 'auto' }}>
                {+(wowmaxResult.data?.priceImpact || 0).toFixed(2)}%
              </span>
            </S.Message>
          )}

          {hasPMMInRoutes && (
            <S.Message $type="info">
              <span>
                The transaction goes via private liquidity pools and requires fast confirmation to
                succeed. See more in{' '}
                <S.ButtonInText onClick={openSettings}>Settings</S.ButtonInText>.
              </span>
            </S.Message>
          )}

          <S.ButtonWrapper>
            {(() => {
              if (!account) {
                return <S.SwapButton onClick={handleConnectClick}>Connect wallet</S.SwapButton>;
              }

              if (requestingChainId) {
                return (
                  <S.SwapButton $disabled $isLoading>
                    ...Network switching
                  </S.SwapButton>
                );
              }

              if (isSwapping) {
                return (
                  <S.SwapButton $disabled $isLoading>
                    ...Swapping
                  </S.SwapButton>
                );
              }

              if (isApproving && isInsufficientAllowance) {
                return (
                  <S.SwapButton $disabled $isLoading>
                    ...Approving
                  </S.SwapButton>
                );
              }

              if (!tokenIn || !tokenOut) {
                return <S.SwapButton $disabled>Select tokens</S.SwapButton>;
              }

              if (isNoValue) {
                return <S.SwapButton $disabled>Enter amount</S.SwapButton>;
              }

              if (isInsufficientBalance) {
                return <S.SwapButton $disabled>Insufficient balance</S.SwapButton>;
              }

              if (isWrapUnwrap && amountOut) {
                return <S.SwapButton onClick={handleSwapClick}>Swap</S.SwapButton>;
              }

              if (wowmaxResult.isLoading && !wowmaxResult.data) {
                return <S.SwapButton $disabled>...Loading</S.SwapButton>;
              }

              if (!isFromNative && isInsufficientAllowance) {
                return (
                  <>
                    <S.SwapButton onClick={handleApproveClick}>Approve</S.SwapButton>
                    <S.ApproveCheckbox>
                      <Checkbox
                        checked={isApproveInfinite}
                        onChange={() => setIsApproveInfinite(!isApproveInfinite)}
                        text="Infinite amount"
                      />
                    </S.ApproveCheckbox>
                  </>
                );
              }

              if (wowmaxResult.isError) {
                return <S.SwapButton $disabled>Error</S.SwapButton>;
              }

              if (wowmaxResult.data === null) {
                return <S.SwapButton $disabled>&nbsp;</S.SwapButton>;
              }

              if (wowmaxResult.data.amountOut === '0') {
                return (
                  <S.SwapButton $disabled>
                    {cutTokenSymbol(tokenOut.symbol)} output is 0
                  </S.SwapButton>
                );
              }

              return (
                <S.SwapButton onClick={handleSwapClick}>
                  {isPriceImpactHigh ? 'Swap anyway' : 'Swap'}
                </S.SwapButton>
              );
            })()}
          </S.ButtonWrapper>
        </S.Panel>
      </S.PanelWrapper>

      <TokenSelectModal />
      <RoutingModal />
      <SettingsModal />
    </S.Overlay>
  );
});
